/**
 * Inventory screen — a toggled GameWindow (INV button / I key) with two
 * sections: the equipped WEAPONS and the DUNGEON LOOT the player has collected
 * (mop, keys, …). Rebuilds its innerHTML each frame while open, matching the
 * side/focus panel convention. No PixiJS.
 */

import type { Player } from '../sim/player';
import type { GameWindow } from './window';

function hex(color: number): string {
  return '#' + (color >>> 0).toString(16).padStart(6, '0').slice(-6);
}

export class InventoryWindow {
  private readonly root: HTMLElement;
  private readonly window: GameWindow;
  private open = false;

  constructor(win: GameWindow) {
    this.window = win;
    this.root = document.createElement('div');
    this.root.className = 'inv-root';
    this.window.body.appendChild(this.root);
    this.window.root.style.display = 'none';
  }

  isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    this.open = !this.open;
    this.window.root.style.display = this.open ? '' : 'none';
  }

  update(player: Player): void {
    if (!this.open) return;
    const weapons = player.weapons
      .map((w, i) => {
        const active = i === player.activeWeaponIdx ? ' inv-active' : '';
        const ammo = w.isMelee
          ? w.magazine !== undefined
            ? `${w.ammo ?? 0}/${w.magazine}`
            : '∞'
          : `${w.ammo ?? 0}/${w.magazine ?? 0}`;
        const tag = w.heavyBlunt ? ' <span class="inv-tag">BLUNT</span>' : '';
        return `<div class="inv-row${active}"><span class="inv-name">${w.name}</span><span class="inv-meta">${ammo}${tag}</span></div>`;
      })
      .join('');
    const loot = player.dungeonLoot.length
      ? player.dungeonLoot
          .map((it) => {
            // A key opens its lock (E). Re-locking (L) is free and works on any
            // lock, so it isn't the key's job to advertise.
            const meta = it.kind === 'key' ? 'E to unlock' : it.kind;
            return `<div class="inv-row"><span class="inv-glyph" style="color:${hex(it.color)}">${it.glyph}</span><span class="inv-name">${it.label}</span><span class="inv-meta">${meta}</span></div>`;
          })
          .join('')
      : '<div class="inv-empty">— nothing yet —</div>';
    const tools = `pick ${player.tools.pick ? '✓' : '✗'} · rake ${player.tools.rake ? '✓' : '✗'}`;
    this.root.innerHTML =
      `<div class="inv-section">WEAPONS</div>${weapons}` +
      `<div class="inv-section">DUNGEON LOOT</div>${loot}` +
      `<div class="inv-tools">tools · ${tools}</div>`;
  }
}
